import { FAQItem, SearchResult } from '../types/support.types'

export interface SearchOptions {
  maxResults?: number
  minScore?: number
  includeKeywords?: boolean
  fuzzyMatch?: boolean
}

export interface SearchSuggestion {
  text: string
  type: 'question' | 'keyword' | 'category'
  score: number
}

/**
 * Advanced search scoring algorithm
 */
export class SearchEngine {
  private static readonly WEIGHTS = {
    QUESTION_EXACT: 100,
    QUESTION_PARTIAL: 50,
    ANSWER_EXACT: 30,
    ANSWER_PARTIAL: 15,
    KEYWORD_EXACT: 40,
    KEYWORD_PARTIAL: 20,
    PRIORITY_BOOST: 10
  }

  /**
   * Search FAQs with advanced scoring
   */
  static searchFAQs(
    faqs: FAQItem[], 
    query: string, 
    options: SearchOptions = {}
  ): SearchResult[] {
    const {
      maxResults = 20,
      minScore = 5,
      includeKeywords = true,
      fuzzyMatch = true
    } = options

    if (!query.trim()) return []

    const searchTerms = this.normalizeQuery(query)
    const results: SearchResult[] = []

    for (const faq of faqs) {
      const score = this.calculateScore(faq, searchTerms, includeKeywords, fuzzyMatch)
      
      if (score >= minScore) {
        const matchedFields = this.getMatchedFields(faq, searchTerms, includeKeywords)
        
        results.push({
          item: faq,
          matchScore: score,
          matchedFields
        })
      }
    }

    // Sort by score (descending) and then by priority
    return results
      .sort((a, b) => {
        if (b.matchScore !== a.matchScore) {
          return b.matchScore - a.matchScore
        }
        return (a.item.priority || 999) - (b.item.priority || 999)
      })
      .slice(0, maxResults)
  }

  /**
   * Generate search suggestions based on FAQ content
   */
  static generateSuggestions(
    faqs: FAQItem[],
    query: string,
    maxSuggestions: number = 5
  ): SearchSuggestion[] {
    if (!query.trim() || query.length < 2) return []

    const suggestions: SearchSuggestion[] = []
    const queryLower = query.toLowerCase().trim()
    const seen = new Set<string>()

    // Extract keywords from all FAQs
    const allKeywords = faqs.flatMap(faq => faq.keywords || [])
    const questionWords = faqs.flatMap(faq => 
      faq.question.toLowerCase().split(/\s+/).filter(word => word.length > 3)
    )

    // Search in keywords
    for (const keyword of allKeywords) {
      if (keyword.toLowerCase().includes(queryLower) && !seen.has(keyword)) {
        suggestions.push({
          text: keyword,
          type: 'keyword',
          score: this.calculateSuggestionScore(keyword, queryLower)
        })
        seen.add(keyword)
      }
    }

    // Search in question words
    for (const word of questionWords) {
      if (word.includes(queryLower) && !seen.has(word) && word !== queryLower) {
        suggestions.push({
          text: word,
          type: 'question',
          score: this.calculateSuggestionScore(word, queryLower)
        })
        seen.add(word)
      }
    }

    // Search for partial question matches
    for (const faq of faqs) {
      const question = faq.question.toLowerCase()
      if (question.includes(queryLower) && !seen.has(faq.question)) {
        suggestions.push({
          text: faq.question,
          type: 'question',
          score: this.calculateSuggestionScore(question, queryLower) + 20 // Boost full questions
        })
        seen.add(faq.question)
      }
    }

    return suggestions
      .sort((a, b) => b.score - a.score)
      .slice(0, maxSuggestions)
  }

  /**
   * Get popular search terms (simulated for now)
   */
  static getPopularSearches(): string[] {
    return [
      'onboarding process',
      'payment',
      'industries supported',
      'OTP verification',
      'dashboard access',
      'pricing plans',
      'business profile',
      'competitive intelligence'
    ]
  }

  /**
   * Normalize search query
   */
  private static normalizeQuery(query: string): string[] {
    return query
      .toLowerCase()
      .trim()
      .replace(/[^\w\s]/g, ' ') // Remove special characters
      .split(/\s+/)
      .filter(term => term.length > 1)
  }

  /**
   * Calculate search score for an FAQ item
   */
  private static calculateScore(
    faq: FAQItem,
    searchTerms: string[],
    includeKeywords: boolean,
    fuzzyMatch: boolean
  ): number {
    let score = 0
    const question = faq.question.toLowerCase()
    const answer = faq.answer.toLowerCase()
    const keywords = (faq.keywords || []).map(k => k.toLowerCase())

    for (const term of searchTerms) {
      // Question matching
      if (question.includes(term)) {
        const isExact = question.split(/\s+/).includes(term)
        score += isExact ? this.WEIGHTS.QUESTION_EXACT : this.WEIGHTS.QUESTION_PARTIAL
      }

      // Answer matching
      if (answer.includes(term)) {
        const isExact = answer.split(/\s+/).includes(term)
        score += isExact ? this.WEIGHTS.ANSWER_EXACT : this.WEIGHTS.ANSWER_PARTIAL
      }

      // Keyword matching
      if (includeKeywords) {
        for (const keyword of keywords) {
          if (keyword.includes(term)) {
            const isExact = keyword === term
            score += isExact ? this.WEIGHTS.KEYWORD_EXACT : this.WEIGHTS.KEYWORD_PARTIAL
          }
        }
      }

      // Fuzzy matching for typos (simple Levenshtein distance)
      if (fuzzyMatch) {
        score += this.calculateFuzzyScore(term, question, answer, keywords)
      }
    }

    // Priority boost (higher priority = lower number = higher boost)
    if (faq.priority && faq.priority <= 5) {
      score += this.WEIGHTS.PRIORITY_BOOST * (6 - faq.priority)
    }

    // Multi-term bonus (if query matches multiple terms)
    if (searchTerms.length > 1) {
      const matchingTerms = searchTerms.filter(term => 
        question.includes(term) || answer.includes(term) || 
        keywords.some(k => k.includes(term))
      )
      if (matchingTerms.length > 1) {
        score += matchingTerms.length * 5
      }
    }

    return Math.round(score)
  }

  /**
   * Get fields that matched the search
   */
  private static getMatchedFields(
    faq: FAQItem,
    searchTerms: string[],
    includeKeywords: boolean
  ): string[] {
    const matched: string[] = []
    const question = faq.question.toLowerCase()
    const answer = faq.answer.toLowerCase()
    const keywords = (faq.keywords || []).map(k => k.toLowerCase())

    const hasMatch = (text: string) => 
      searchTerms.some(term => text.includes(term))

    if (hasMatch(question)) matched.push('question')
    if (hasMatch(answer)) matched.push('answer')
    if (includeKeywords && keywords.some(k => hasMatch(k))) {
      matched.push('keywords')
    }

    return matched
  }

  /**
   * Simple fuzzy matching score
   */
  private static calculateFuzzyScore(
    term: string,
    question: string,
    answer: string,
    keywords: string[]
  ): number {
    let score = 0
    const threshold = 0.8 // Similarity threshold

    // Check question words
    const questionWords = question.split(/\s+/)
    for (const word of questionWords) {
      if (word.length > 3 && this.similarity(term, word) > threshold) {
        score += 5
      }
    }

    // Check keywords
    for (const keyword of keywords) {
      if (this.similarity(term, keyword) > threshold) {
        score += 10
      }
    }

    return score
  }

  /**
   * Calculate string similarity (simple implementation)
   */
  private static similarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2
    const shorter = str1.length > str2.length ? str2 : str1
    
    if (longer.length === 0) return 1.0

    const editDistance = this.levenshteinDistance(longer, shorter)
    return (longer.length - editDistance) / longer.length
  }

  /**
   * Levenshtein distance for fuzzy matching
   */
  private static levenshteinDistance(str1: string, str2: string): number {
    const matrix = []

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i]
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1]
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          )
        }
      }
    }

    return matrix[str2.length][str1.length]
  }

  /**
   * Calculate suggestion score
   */
  private static calculateSuggestionScore(suggestion: string, query: string): number {
    if (suggestion.toLowerCase().startsWith(query.toLowerCase())) {
      return 50 + (query.length / suggestion.length) * 50
    }
    
    if (suggestion.toLowerCase().includes(query.toLowerCase())) {
      return 30 + (query.length / suggestion.length) * 30
    }
    
    return this.similarity(suggestion.toLowerCase(), query.toLowerCase()) * 20
  }
}

/**
 * Utility functions for search highlighting
 */
export class HighlightUtils {
  /**
   * Highlight search terms in text - returns highlighted parts for React rendering
   */
  static highlightText(text: string, query: string): Array<{text: string, isHighlighted: boolean}> {
    if (!query.trim()) return [{text, isHighlighted: false}]

    const terms = query.toLowerCase().split(/\s+/).filter(term => term.length > 1)
    let parts = [{text, isHighlighted: false}]

    // Highlight each term
    for (const term of terms) {
      const newParts: Array<{text: string, isHighlighted: boolean}> = []
      
      for (const part of parts) {
        if (!part.isHighlighted) {
          const regex = new RegExp(`(${term})`, 'gi')
          const splitParts = part.text.split(regex)
          
          for (let i = 0; i < splitParts.length; i++) {
            if (splitParts[i] && splitParts[i].toLowerCase() === term.toLowerCase()) {
              newParts.push({text: splitParts[i], isHighlighted: true})
            } else if (splitParts[i]) {
              newParts.push({text: splitParts[i], isHighlighted: false})
            }
          }
        } else {
          newParts.push(part)
        }
      }
      
      parts = newParts
    }

    return parts
  }

  /**
   * Get snippet from text around search terms
   */
  static getSearchSnippet(text: string, query: string, maxLength: number = 200): string {
    if (!query.trim()) return text.slice(0, maxLength)

    const terms = query.toLowerCase().split(/\s+/)
    const textLower = text.toLowerCase()
    
    // Find the first occurrence of any search term
    let firstMatch = -1
    for (const term of terms) {
      const index = textLower.indexOf(term)
      if (index !== -1 && (firstMatch === -1 || index < firstMatch)) {
        firstMatch = index
      }
    }

    if (firstMatch === -1) return text.slice(0, maxLength)

    // Calculate snippet boundaries
    const start = Math.max(0, firstMatch - Math.floor(maxLength / 2))
    const end = Math.min(text.length, start + maxLength)
    
    let snippet = text.slice(start, end)
    
    // Add ellipsis if truncated
    if (start > 0) snippet = '...' + snippet
    if (end < text.length) snippet = snippet + '...'
    
    return snippet
  }
}